import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { callClaude } from '../utils/claudeProxy';
import {
  Box, Typography, Button, CircularProgress, Alert, LinearProgress,
  TextField, IconButton, Chip, Tooltip, Collapse
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function extractBidFormFromPDF(base64) {
  const data = await callClaude({
      model: 'claude-opus-5',
      max_tokens: 8000,
      messages: [{
        role: 'user',
        content: [
          {
            type: 'document',
            source: { type: 'base64', media_type: 'application/pdf', data: base64 }
          },
          {
            type: 'text',
            text: `Analysera detta upphandlingsdokument (anbudsformulär, förfrågningsunderlag eller liknande LOU-dokument) och extrahera alla avsnitt och fält där anbudsgivaren ska lämna information, bekräfta krav eller fylla i uppgifter.

Dokumentet kan innehålla sektioner som: AUA (administrativa uppgifter), AUB (bevis/intyg), AUC (priser), ESPD (europeisk försäkran), kravspecifikation, prisbilaga m.m. — alla dessa ska inkluderas.

Returnera ENDAST ett JSON-objekt, ingen annan text:
{
  "sections": [
    {
      "title": "Sektionens rubrik",
      "fields": [
        {
          "label": "Fältets beteckning",
          "type": "text",
          "hint": "Eventuell typanvisning (t.ex. Fritext, Ja/Nej, ÅÅÅÅ-MM-DD, timkostnad)",
          "answer": "",
          "isContactField": false
        }
      ]
    }
  ]
}

Regler:
- type = "text" för fritext-fält och prisfält (skriv befintligt svar som answer om det finns)
- type = "yesno" för Ja/Nej-fält och SKA-krav (bekräftelse att krav uppfylls); fyll i befintligt svar om det finns
- type = "file" för krav på bifogad handling (answer = null)
- isContactField = true om fältet är kontaktinfo: företagsnamn, namn, mobilnummer, e-post, ort, datum, befattning, organisationsnr, behörig företrädare
- Inkludera SKA-krav (obligatoriska krav) som yesno-fält där anbudsgivaren bekräftar uppfyllnad
- Inkludera BÖR-krav (mervärdeskrav/utvärderingskriterier) som text-fält med svarsmöjlighet
- Inkludera alla prisposter och resurskategorier från prisbilaga eller prislista
- Ta med ALLA fält, inklusive de som redan är ifyllda i formuläret
- Använd fältets exakta rubrik eller beteckning som label`
          }
        ]
      }]
    });
  const raw = data.content[0].text;
  const clean = raw.replace(/```json\s*|```/g, '').trim();
  let parsed;
  try {
    parsed = JSON.parse(clean);
  } catch (e) {
    console.error('JSON-parsningsfel. Råsvar:', raw.slice(0, 500));
    throw new Error(`Kunde inte tolka AI-svar som JSON. Modellen returnerade: ${raw.slice(0, 200)}`);
  }
  return parsed.sections || [];
}

export default function TenderBidForm({ tender, phase, phaseIdx, onUpdate, collapsed = false, onToggleCollapse }) {
  const [localSections, setLocalSections] = useState(tender.bidFormSections || []);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [copied, setCopied] = useState(null);
  const sectionsRef = useRef(localSections);

  useEffect(() => {
    setLocalSections(tender.bidFormSections || []);
  }, [tender.bidFormSections]);

  useEffect(() => {
    sectionsRef.current = localSections;
  }, [localSections]);

  const totalTextFields = localSections.reduce(
    (sum, s) => sum + s.fields.filter(f => f.type !== 'file').length, 0
  );
  const answeredFields = localSections.reduce(
    (sum, s) => sum + s.fields.filter(f => f.type !== 'file' && f.answer).length, 0
  );

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setAnalyzing(true);
    setError('');
    setProgress(20);
    try {
      const base64 = await fileToBase64(file);
      setProgress(50);
      const extracted = await extractBidFormFromPDF(base64);
      setProgress(80);

      // Komplettera requiredDocuments med bifogad-fil-krav från formuläret
      const fileLabels = extracted.flatMap(s =>
        s.fields.filter(f => f.type === 'file').map(f => f.label)
      );
      const existingDocs = (phase || tender).requiredDocuments || [];
      const newDocs = fileLabels.filter(label =>
        !existingDocs.some(d =>
          d.toLowerCase().includes(label.toLowerCase().slice(0, 25)) ||
          label.toLowerCase().includes(d.toLowerCase().slice(0, 25))
        )
      );

      // Bygg Firestore-uppdatering (fas-medveten)
      const firestoreUpdate = { bidFormSections: extracted };
      let updatedTender = { ...tender, bidFormSections: extracted };

      if (newDocs.length > 0) {
        const updatedDocs = [...existingDocs, ...newDocs];
        if (tender.phases?.length > 0) {
          const newPhases = tender.phases.map((p, i) =>
            i === phaseIdx ? { ...p, requiredDocuments: updatedDocs } : p
          );
          firestoreUpdate.phases = newPhases;
          updatedTender = { ...updatedTender, phases: newPhases };
        } else {
          firestoreUpdate.requiredDocuments = updatedDocs;
          updatedTender = { ...updatedTender, requiredDocuments: updatedDocs };
        }
      }

      await updateDoc(doc(db, 'tenders', tender.id), firestoreUpdate);
      onUpdate(updatedTender);
      setLocalSections(extracted);
      setProgress(100);
      const msg = newDocs.length > 0
        ? `${extracted.length} sektioner extraherade · ${newDocs.length} handlingar tillagda i checklistan.`
        : `${extracted.length} sektioner extraherade.`;
      setSaved('Anbudsformulär-PDF analyserat — ' + msg);
      setTimeout(() => setSaved(''), 5000);
    } catch (err) {
      console.error(err);
      setError(`Fel: ${err?.message || 'Okänt fel'}`);
    } finally {
      setAnalyzing(false);
      setProgress(0);
      e.target.value = '';
    }
  }

  function handleFieldChange(si, fi, value) {
    setLocalSections(prev => prev.map((s, i) => i !== si ? s : {
      ...s,
      fields: s.fields.map((f, j) => j !== fi ? f : { ...f, answer: value })
    }));
  }

  async function handleFieldBlur() {
    try {
      await updateDoc(doc(db, 'tenders', tender.id),
        { bidFormSections: sectionsRef.current });
      onUpdate({ ...tender, bidFormSections: sectionsRef.current });
    } catch (err) {
      console.error('Kunde inte spara svar:', err);
    }
  }

  function copyToClipboard(text, key) {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box onClick={onToggleCollapse} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none', flex: 1, '&:hover': { opacity: 0.75 } }}>
          <ExpandMoreIcon sx={{ fontSize: 14, color: '#7b1fa2',
            transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 0.2s', flexShrink: 0 }} />
          <AssignmentIcon sx={{ fontSize: 16, color: '#7b1fa2' }} />
          <Typography variant="caption" fontWeight="bold" sx={{ color: '#7b1fa2' }}>
            ANBUDSFORMULÄR-PDF
          </Typography>
          {totalTextFields > 0 && (
            <Chip
              label={`${answeredFields}/${totalTextFields} besvarade`}
              size="small"
              sx={{
                fontSize: 10, height: 18,
                bgcolor: answeredFields === totalTextFields
                  ? 'rgba(46,125,50,0.15)' : 'rgba(123,31,162,0.12)',
                color: answeredFields === totalTextFields ? '#2e7d32' : '#7b1fa2'
              }}
            />
          )}
        </Box>
        <Button size="small" component="label" variant="outlined"
          startIcon={analyzing
            ? <CircularProgress size={12} />
            : <AutoFixHighIcon sx={{ fontSize: 14 }} />}
          disabled={analyzing}
          sx={{
            fontSize: '0.65rem', py: 0.2, px: 1,
            borderColor: '#7b1fa2', color: '#7b1fa2',
            '&:hover': { borderColor: '#6a1b9a', bgcolor: 'rgba(123,31,162,0.06)' }
          }}>
          {analyzing ? 'Analyserar...' : localSections.length > 0 ? 'Uppdatera formulär' : 'Ladda upp formulär-PDF'}
          <input type="file" hidden accept=".pdf" onChange={handleUpload} />
        </Button>
      </Box>

      <Collapse in={!collapsed}>
      {analyzing && (
        <LinearProgress variant="determinate" value={progress}
          sx={{ mb: 1, '& .MuiLinearProgress-bar': { bgcolor: '#7b1fa2' } }} />
      )}
      {error && <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 1, py: 0.5 }}>{saved}</Alert>}

      {localSections.length === 0 && !analyzing && (
        <Box sx={{ py: 1.5, px: 1, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Ladda upp PDF-utskrift av anbudsformuläret — AI extraherar alla fält och lägger till saknade handlingar i checklistan
          </Typography>
        </Box>
      )}

      {localSections.map((section, si) => (
        <Box key={si} sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary"
            sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.6,
              fontSize: '0.65rem' }}>
            {section.title}
          </Typography>

          {section.fields.map((field, fi) => {
            const key = `${si}_${fi}`;

            if (field.type === 'file') {
              return (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                  py: 0.25, opacity: 0.55 }}>
                  <InsertDriveFileIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    {field.label}
                  </Typography>
                  <Chip label="Bifogad fil" size="small"
                    sx={{ fontSize: 9, height: 14, bgcolor: 'action.hover' }} />
                </Box>
              );
            }

            return (
              <Box key={key} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}
                    sx={{ fontSize: '0.75rem' }}>
                    {field.label}
                  </Typography>
                  {field.isContactField && (
                    <Chip label="Kontakt" size="small"
                      sx={{ fontSize: 9, height: 14,
                        bgcolor: 'rgba(21,101,192,0.1)', color: '#1565C0' }} />
                  )}
                  {field.hint && field.hint !== 'Fritext' && (
                    <Typography variant="caption"
                      sx={{ color: 'text.disabled', fontSize: '0.62rem' }}>
                      · {field.hint}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                  <TextField
                    size="small" fullWidth multiline
                    minRows={field.type === 'yesno' ? 1 : 2}
                    maxRows={10}
                    value={localSections[si]?.fields[fi]?.answer || ''}
                    onChange={e => handleFieldChange(si, fi, e.target.value)}
                    onBlur={handleFieldBlur}
                    placeholder={field.type === 'yesno' ? 'Ja / Nej' : 'Skriv svar...'}
                    sx={{
                      '& .MuiInputBase-input': { fontSize: '0.82rem' },
                      '& .MuiInputBase-root': {
                        bgcolor: localSections[si]?.fields[fi]?.answer
                          ? 'transparent' : 'action.hover'
                      }
                    }}
                  />
                  <Tooltip title={copied === key ? 'Kopierat!' : 'Kopiera'}>
                    <span>
                      <IconButton size="small"
                        disabled={!localSections[si]?.fields[fi]?.answer}
                        onClick={() => copyToClipboard(
                          localSections[si]?.fields[fi]?.answer || '', key)}
                        sx={{
                          mt: 0.3, border: '1px solid', p: 0.5,
                          borderColor: copied === key ? '#2e7d32' : 'divider',
                          color: copied === key ? '#2e7d32' : 'text.secondary',
                        }}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
      </Collapse>
    </Box>
  );
}
